import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, KeyRound, Lock, Unlock, Power, Trash2, ShieldCheck, Copy, X, UserPlus, Settings2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "../../components/forms/FormBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import PermissionMatrix from "./PermissionMatrix";

const Badge = ({ ok, on, off }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ok ? "bg-success/15 text-success" : "bg-destructive/10 text-destructive"}`}>{ok ? on : off}</span>
);

export default function UserManagement({ catalog }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role_id: "" });
  const [tempPw, setTempPw] = useState(null);
  const [editUser, setEditUser] = useState(null); // {user, overrides}

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/admin/users"), api.get("/admin/roles")]);
      setUsers(u.data); setRoles(r.data);
    } catch (e) { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    try {
      const { data } = await api.post("/admin/users", form);
      setTempPw({ email: data.email, pw: data.temp_password });
      setCreateOpen(false); setForm({ name: "", email: "", role_id: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (u, fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetPw = (u) => act(u, async () => {
    const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
    setTempPw({ email: u.email, pw: data.temp_password });
  }, "Password reset");

  const saveEdit = async () => {
    try {
      await api.put(`/admin/users/${editUser.user.id}`, {
        role_id: editUser.user.role_id || null,
        permission_overrides: editUser.overrides,
      });
      toast.success("User permissions updated"); setEditUser(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-panel">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-navy">User Management</h2>
          <p className="mt-1 text-sm text-muted-foreground">Create unlimited staff accounts, assign roles/permissions, reset passwords, lock or disable access.</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="user-create-open">
          <UserPlus className="h-4 w-4" /> New User
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div> : (
        <div className="overflow-hidden rounded-2xl border border-border bg-white">
          <table className="w-full text-sm">
            <thead><tr className="bg-navy/[0.03] text-left text-xs uppercase tracking-wider text-charcoal/60">
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">2FA</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-border/60" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-navy">{u.name || "—"} {u.is_owner && <span className="ml-1 rounded bg-gold/15 px-1.5 py-0.5 text-[10px] font-bold text-gold-dark">OWNER</span>}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </td>
                  <td className="px-4 py-3 text-charcoal">{u.role_name}</td>
                  <td className="px-4 py-3"><Badge ok={u.is_active && !u.locked} on={u.locked ? "Locked" : "Active"} off={u.locked ? "Locked" : "Disabled"} /></td>
                  <td className="px-4 py-3">{u.twofa_enabled ? <ShieldCheck className="h-4 w-4 text-success" /> : <span className="text-xs text-charcoal/40">Off</span>}</td>
                  <td className="px-4 py-3">
                    {u.is_owner ? <span className="text-xs text-charcoal/40">Protected</span> : (
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <button onClick={() => setEditUser({ user: u, overrides: u.permission_overrides || {} })} title="Edit role & permissions" className="rounded-md border border-border p-1.5 text-navy hover:border-navy" data-testid={`user-edit-${u.email}`}><Settings2 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => resetPw(u)} title="Reset password" className="rounded-md border border-border p-1.5 text-navy hover:border-navy" data-testid={`user-reset-${u.email}`}><KeyRound className="h-3.5 w-3.5" /></button>
                        <button onClick={() => act(u, () => api.post(`/admin/users/${u.id}/status`, { locked: !u.locked }), u.locked ? "Unlocked" : "Locked")} title={u.locked ? "Unlock" : "Lock"} className="rounded-md border border-border p-1.5 text-navy hover:border-navy" data-testid={`user-lock-${u.email}`}>{u.locked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}</button>
                        <button onClick={() => act(u, () => api.post(`/admin/users/${u.id}/status`, { is_active: !u.is_active }), u.is_active ? "Disabled" : "Activated")} title={u.is_active ? "Deactivate" : "Activate"} className={`rounded-md border p-1.5 hover:border-navy ${u.is_active ? "border-border text-charcoal" : "border-success text-success"}`} data-testid={`user-toggle-${u.email}`}><Power className="h-3.5 w-3.5" /></button>
                        <button onClick={() => { if (window.confirm(`Delete ${u.email}? This cannot be undone.`)) act(u, () => api.delete(`/admin/users/${u.id}`), "User deleted"); }} title="Delete" className="rounded-md border border-destructive/40 p-1.5 text-destructive hover:bg-destructive/5" data-testid={`user-delete-${u.email}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create user */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy">Create User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="mb-1 block text-sm font-semibold text-charcoal">Full name</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></div>
            <div><label className="mb-1 block text-sm font-semibold text-charcoal">Email</label><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" /></div>
            <div><label className="mb-1 block text-sm font-semibold text-charcoal">Role</label>
              <select className={inputCls} value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="user-form-role">
                <option value="">— No role (custom permissions only) —</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <p className="text-xs text-muted-foreground">A secure temporary password will be generated. The user must reset it on first login.</p>
            <button onClick={createUser} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="user-form-submit"><Plus className="h-4 w-4" /> Create User</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Temp password reveal */}
      <Dialog open={!!tempPw} onOpenChange={(o) => !o && setTempPw(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy">Temporary Password</DialogTitle></DialogHeader>
          {tempPw && (
            <div className="space-y-3">
              <p className="text-sm text-charcoal">Share this one-time password with <b>{tempPw.email}</b>. It won't be shown again.</p>
              <div className="flex items-center justify-between rounded-lg border border-border bg-navy/[0.03] px-4 py-3">
                <code className="text-sm font-bold text-navy" data-testid="temp-password-value">{tempPw.pw}</code>
                <button onClick={() => { navigator.clipboard.writeText(tempPw.pw); toast.success("Copied"); }} className="text-navy hover:text-gold"><Copy className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit role + overrides */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle className="font-serif text-navy">Permissions — {editUser?.user?.email}</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-5">
              <div>
                <label className="mb-1 block text-sm font-semibold text-charcoal">Base Role</label>
                <select className={inputCls} value={editUser.user.role_id || ""} onChange={(e) => setEditUser({ ...editUser, user: { ...editUser.user, role_id: e.target.value } })} data-testid="user-edit-role">
                  <option value="">— No role —</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <p className="mb-1 text-sm font-semibold text-charcoal">User-level permission overrides</p>
                <p className="mb-3 text-xs text-muted-foreground">Overrides replace the role's actions for that module. Leave empty to inherit the role.</p>
                <PermissionMatrix modules={catalog.modules} actions={catalog.actions} value={editUser.overrides} onChange={(v) => setEditUser({ ...editUser, overrides: v })} />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setEditUser(null)} className="rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal">Cancel</button>
                <button onClick={saveEdit} className="rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="user-edit-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
